import { BehaviorSubject, PartialObserver, Subscription } from 'rxjs';
import { IRSRequestQuery } from 'src/app/interfaces/core';
import { ApiService } from 'src/app/services/api/core/api.service';
import { ApiEndpoints } from 'src/app/services/api/endpoints/endpoints';
import { cleanObj, deepFind, firstValueFrom } from './RSCUtils';

const apiReference$: BehaviorSubject<ApiService> =
  new BehaviorSubject<ApiService>({} as any);
const accountType$: BehaviorSubject<any> = new BehaviorSubject<any>('');

const _cacheSubscriptions: any = {};

const buildRequestParams = (map: any, source: any) => {
  let retVal: any = {};
  for (let key in map) {
    if (typeof map[key] === 'string') {
      // proceed to find value
      const value = deepFind(source, map[key]);
      if (typeof value !== 'function' /*  && typeof value !== 'object' */) {
        retVal[key] = value;
      } /* else if (Array.isArray(value)) {
                retVal[key] = JSON.parse(JSON.stringify(value));
            } */
    } else if (typeof map[key] === 'object') {
      // is child proceed to deep
      const params = buildRequestParams(map[key], source);
      if (params) {
        retVal[key] = params;
      }
    } // check function
  }
  return cleanObj(retVal);
};

export interface IRSCBaseInstanceParams {
  //api: any,
  fillWith?: any;
  query?: IRSRequestQuery;
  params?: any;
  defaultData?: any;
  endpoints?: any; // objeto de IRSCEndpoint
}

export interface IRSCEndpoint {
  path?: string;
  ep?: any;
  params?: any;
  _before?: any;
  _after?: any;
  autoParams?: any;
}

const __apiRequest = (
  resolve: any,
  reject: any,
  _self: any,
  endPoint: IRSCEndpoint,
  params?: any,
) => {
  try {
    const _endpoint = endPoint.ep
      ? endPoint.ep
      : deepFind(
          ApiEndpoints || {},
          (endPoint.path || '').replace('{at}', accountType$.value),
        );
    if (endPoint.autoParams) {
      const autoParams = buildRequestParams(endPoint.autoParams, _self);
      if (Object.keys(autoParams).length !== 0) {
        params = { ...(params || {}), ...autoParams };
      }
    }
    if (_endpoint) {
      if (endPoint._before) {
        ({ params } = endPoint._before(params, _self));
      }

      firstValueFrom(apiReference$.value.request(_endpoint, params))
        .then(resolve)
        .catch((error: any) => reject(error?.error?.result || error));
    } else {
      reject({ message: 'REQUEST_NOT_EXIST', request: endPoint });
    }
  } catch (e: any) {
    reject({
      message: 'SYNTAX_ERROR_IN_REQUEST',
      request: endPoint,
      error: e.message,
    });
  }
};

const _checkAccountType = (
  resolve: any,
  reject: any,
  _self: any,
  endPoint: IRSCEndpoint,
  params?: any,
) => {
  if (accountType$.value?.length > 0) {
    __apiRequest(resolve, reject, _self, endPoint, params);
  } else {
    let _type = '_';
    const subscription = RSCBase.accountType.subscribe({
      next: (type: any) => {
        if (type?.length > 0 && _type != type) {
          _type = type;
          subscription.unsubscribe();
          __apiRequest(resolve, reject, _self, endPoint, params);
        }
      },
    });
  }
};

const _checkApiReference = (
  resolve: any,
  reject: any,
  _self: any,
  endPoint: IRSCEndpoint,
  params?: any,
) => {
  if (!!apiReference$.value?.request) {
    _checkAccountType(resolve, reject, _self, endPoint, params);
  } else {
    const subscription = RSCBase.apiReference.subscribe({
      next: (ref: any) => {
        if (ref?._request) {
          subscription.unsubscribe();
          _checkAccountType(resolve, reject, _self, endPoint, params);
        }
      },
    });
  }
};

const _apiRequest = (_self: any, endPoint: IRSCEndpoint, params?: any) =>
  new Promise((resolve, reject) =>
    _checkApiReference(resolve, reject, _self, endPoint, params),
  );

export class RSCBase {
  protected subscriptions$: BehaviorSubject<any> = new BehaviorSubject<any>({});

  public loading = false;

  constructor(protected cfg: IRSCBaseInstanceParams) {
    // check auto fill with params
    if (cfg?.fillWith) {
      this._fill(cfg?.fillWith);
    } else {
      this._fill(cfg?.defaultData);
    }
  }

  async Save(params: any = {}) {
    if (
      (this as any).id > 0 ||
      (this as any).admin_account?.id > 0 ||
      (this as any).user?.id > 0
    ) {
      return this.Edit(params);
    } else {
      return this.Create(params);
    }
  }

  async Edit(params: any = {}) {
    return this._request(this.cfg.endpoints.edit, params);
  }

  async Create(params: any = {}) {
    return this._request(this.cfg.endpoints.create, params);
  }

  async Delete(params: any = {}) {
    return this._request(this.cfg.endpoints.delete, params);
  }

  async Load(endPoint: IRSCEndpoint, params: { query: IRSRequestQuery } | any) {
    return this._request(endPoint, params);
  }

  async DownloadRegistry(params: { query: IRSRequestQuery } | any) {
    return this._request(this.cfg.endpoints.downloadRegistry, params);
  }

  static GetInstanceSync(params: { query: IRSRequestQuery } | any) {
    const instance = new (this as any)({});
    const promise = new Promise((resolve, reject) => {
      instance
        ._request(instance.cfg.endpoints.get, params)
        .then((result: any) => {
          if (result?.records) {
            instance._fill(result?.records[0]);
          } else {
            instance._fill(result[0]);
          }
          resolve(result);
        })
        .catch(reject);
    })
      .then(() => {})
      .catch(() => {});
    return instance;
  }

  static GetInstance(params: { query: IRSRequestQuery } | any) {
    return new Promise((resolve, reject) => {
      const instance = new (this as any)({});
      instance
        ._request(instance.cfg.endpoints.get, params)
        .then((result: any) => {
          if (result?.records) {
            instance._fill(result?.records[0]);
          } else {
            instance._fill(result[0]);
          }
          resolve(instance);
        })
        .catch(reject);
    });
  }

  static GetInstances(params: { query: IRSRequestQuery } | any) {
    const dummy = new (this as any)({});
    return new Promise((resolve, reject) => {
      dummy
        ._request(dummy.cfg.endpoints.get, params)
        .then((result: any) => {
          result = result?.records || result;
          const retVal = [];
          if (Array.isArray(result) && result.length > 0) {
            for (let fillWith of result) {
              retVal.push(new (this as any)({ fillWith }));
            }
          }
          resolve(retVal);
        })
        .catch(reject);
    });
  }

  Instance(params: { query: any } | any) {
    return this._request(this.cfg.endpoints.get, params).then((result: any) => {
      if (result?.records) {
        this._fill(result?.records[0]);
      } else {
        this._fill(result[0]);
      }
      return this;
    });
  }

  _fill(data: any) {
    data = data || {};
    this._beforeFill(data);
    for (let key in data) {
      (this as any)[key] = data[key];
    }
    this._afterFill(data);
    return this._notifyPropertyChange();
  }

  _notifyPropertyChange() {
    this.subscriptions$.next(this);
    return this;
  }

  protected _afterFill(data: any) {}

  protected _beforeFill(data: any) {}

  protected _request(endPoint: IRSCEndpoint, params?: any) {
    this.loading = true;
    return new Promise((resolve, reject) => {
      _apiRequest(this, endPoint, params)
        .then(resolve)
        .catch((error: any) => {
          this._errorCallback(error); // handle error toast
          reject(error);
        })
        .finally(() => (this.loading = false));
    });
  }

  protected _errorCallback(error: any) {}

  public Subscribe(
    observer?: PartialObserver<any>,
    identifierCache?: string,
  ): Subscription {
    const subscription = this.subscriptions$.subscribe(observer);
    if (identifierCache?.length) {
      _cacheSubscriptions[identifierCache] = subscription;
    }
    return subscription;
  }

  public Unsubscribe(identifierCache: string) {
    if (_cacheSubscriptions[identifierCache]) {
      /* if (logType == logTypes.vervose) {
                console.log('Unsubscribe:' + identifierCache);
            } */
      _cacheSubscriptions[identifierCache].unsubscribe();
    }
  }

  static Cancel(requestId: any) {
    apiReference$.value.__auth.CancelRequest(requestId);
  }

  public Clone() {
    /* let clone = Object.create(
            // Set the prototype of the new object to the prototype of the instance.
            // Used to allow new object behave like class instance.
            Object.getPrototypeOf(this),
        ); */
    //let clone= this.constructor;
    let clone: any = this.constructor;
    clone = new clone({ fillWith: JSON.parse(JSON.stringify(this)) });
    //clone._fill(JSON.parse(JSON.stringify(this)));
    return clone as any;
  }

  public static apiReference = apiReference$.asObservable();
  public static get apiReferenceSync() {
    return apiReference$.value;
  }
  public static accountType = apiReference$.asObservable();
  public static get accountTypeSync() {
    return accountType$.value;
  }
  public static setApireference = (value: any) => {
    apiReference$.next(value);
  };

  public static setAccountType = (value: any) => {
    console.log('Account type: ' + value);
    accountType$.next(value);
  };

  toJSON(): any {
    throw Error(
      this.constructor.name +
        ' Should implement toJSON to be clonable or to be JSONED',
    );
  }
}
